import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {ADD_TO_CART_ERROR_KEYS, API_ERROR_MESSAGE} from '../../constants'
import {Button, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {useLocation} from 'react-router-dom'
import {useShopperBasketsMutationHelper} from '@salesforce/commerce-sdk-react'
import {useToast} from '../../hooks/use-toast'
import {noop} from '../../utils/utils'
import {
    basic_gtm_event,
    item_gtm_event,
    polish_ecommerce_gtm_event,
} from '../../custom-api/layers/default'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useMaxQuantityItems} from '../../hooks/use-max-quantity-items'
import {getPriceData} from '../../utils/product-utils'
import useUtils from '../../custom-hooks/use-utils-helper'
import {remCalc} from '../../theme/_utils'
import {getOrderAvailabilityStatus} from '../../utils/order-availability-utils'

const AddToCartButton = ({
    product,
    compactView = false,
    setShowErrorMessage,
    errorContainerRef,
    showUniversalFeedback = true,
    variationAttributes = [],
    bundleID = null,
    isSubscription = false,
    callback = noop,
    universalFeedback = false,
    isFromHelpMeChoose = false,
    isPurchaseBuy = false,
    isPreorder = false,
}) => {
    const location = useLocation()
    const intl = useIntl()
    const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false)
    const styles = useMultiStyleConfig('Modal')
    const {addLayer} = useGoogleTagManager()
    const {currency} = useCurrency()
    const {isQuantityAllowed, getMaxQuantityErrorMessage, getMaxQuantity} = useMaxQuantityItems()

    /**************** Add To Cart ****************/

    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper() // Sdk to interact with the cart
    const {data: basket} = useCurrentBasket()
    const showToast = useToast()

    // Function to show an error in toast
    const showError = () => {
        showToast({
            title: intl.formatMessage(API_ERROR_MESSAGE),
            status: 'error',
        })
    }

    const DerivedProductCallback = (product, isProductPartOfSet, isProductPartOfBundle) => {
        return useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)
    }

    // Normalize `product` to an array (to handle both single product and bundle)
    const productsArray = Array.isArray(product) ? product : [product]

    const productData = []

    productsArray.forEach((product) => {
        product.type = product.productType || product.type
        const isProductPartOfSet = product?.type?.set
        const isProductPartOfBundle = product?.type?.bundle

        const masterProduct = {
            ...product,
            ...product.representedProduct,
        }

        const {variant, stockLevel} = DerivedProductCallback(
            product,
            isProductPartOfSet,
            isProductPartOfBundle,
        )

        product.variationAttributes = variationAttributes

        productData.push({
            masterProduct,
            variant,
            stockLevel,
        })
    })

    const addToCart = async (productSelectionValues) => {
        try {
            const productItems = productSelectionValues.flatMap(({variant, quantity, product}) => {
                if (isSubscription) {
                    return (
                        product?.c_productSetProducts?.map((sub) => ({
                            productId: sub?.productId,
                            quantity: sub?.quantity,
                            c_subModelIdentifier: sub?.subModelIdentifier,
                            c_productSetID: product?.id,
                            c_isSubscription: true,
                            c_isSubscriptionRequest: true,
                            optionItems: [
                                {
                                    optionId: 'isSubscription',
                                    optionValueId: 'isSubscription',
                                },
                            ],
                        })) || []
                    )
                } else {
                    return {
                        productId: bundleID ? product?.productId : variant?.productId,
                        price: bundleID ? product.price : variant?.price,
                        quantity,
                        c_MaterialGroup: product?.c_materialGroup,
                        c_categoryName: product?.c_categoryName,
                        c_subModelIdentifier: product?.c_subModelIdentifier,
                        c_prdhaCategory: product?.c_prdhaCategory,
                        ...(!!bundleID && {c_productSetID: bundleID, c_isBundleRequest: true}), // Add only if bundleID exists
                    }
                }
            })

            await addItemToNewOrExistingBasket(productItems)

            // einstein.sendAddToCart(productItems)

            return productSelectionValues
        } catch (error) {
            console.error('error', error)
            showError(error) // Show error on failure
        }
    }

    const {
        isOpen: isAddToCartModalOpen,
        onOpen: onAddToCartModalOpen,
        onClose: onAddToCartModalClose,
    } = useAddToCartModalContext()

    useEffect(() => {
        if (isAddToCartModalOpen && !universalFeedback) {
            onAddToCartModalClose()
        }
    }, [location.pathname])

    // Function to know if the product can be purchased based on quantity and availability
    const validateOrderability = (variant, quantity, stockLevel) => {
        return quantity > 0 && quantity <= stockLevel
    }

    // Function to check if products are valid before adding them to the cart
    const validateAndShowError = () => {
        let hasValidSelection = true
        productData.forEach((product) => {
            const {variant, stockLevel, masterProduct} = product

            // Skip validation for preorder/backorder products
            const orderStatus = getOrderAvailabilityStatus(masterProduct)
            const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

            if (isPreorderOrBackorder) {
                return
            }

            const isValid =
                validateOrderability(variant, 1, stockLevel) ||
                masterProduct?.c_isSubscription === 'true'

            if (!isValid) {
                hasValidSelection = false
                if (!variant) {
                    setShowErrorMessage(ADD_TO_CART_ERROR_KEYS.NO_VARIANT_SELECTED)
                } else {
                    setShowErrorMessage(ADD_TO_CART_ERROR_KEYS.INVENTORY_REMAINING_FOR_PRODUCT)
                }
            }
        })

        // If at least one product is invalid, show the error and scroll to the error section
        if (!hasValidSelection) {
            errorContainerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        }

        return hasValidSelection
    }

    // Function to determine the appropriate item_id based on the condition
    const getItemId = (item) => {
        //when we add the product from a modal we manage with the Variant and not the SKU
        // be sure that the ID of the item is not the ID of the variant
        if (item?.product?.id === item?.product?.c_subModelIdentifier) {
            return item?.variant?.productId
        }
        return item?.product?.id || item?.masterProduct?.representedProduct?.id
    }

    const handleCartItem = async (productData) => {
        // Check quantity limits for each product family
        let quantityError = false
        productData.forEach(({masterProduct, quantity}) => {
            if (!isQuantityAllowed(masterProduct, quantity || 1)) {
                const maxQty = getMaxQuantity(masterProduct)
                showToast({
                    title: getMaxQuantityErrorMessage(maxQty),
                    status: 'error',
                })
                quantityError = true
            }
        })
        if (quantityError) {
            setIsLoadingAddToCart(false)
            return
        }
        // check if there is already a customized product in the cart
        if (basket?.productItems?.[0]?.c_officinaConfigurationId) {
            return showToast({
                title: intl.formatMessage({
                    defaultMessage:
                        'Since you are adding a customized product to your cart, your order cannot include other types of products. Before adding other products to your cart, complete your order.',
                    id: 'officina.error.complete_your_order',
                }),
                status: 'error',
            })
        }

        setIsLoadingAddToCart(true)

        if (!bundleID) {
            const hasValidSelection = validateAndShowError() // Check if the products are valid
            if (!hasValidSelection) {
                setIsLoadingAddToCart(false) // If there are errors, remove the loading and abort the operation
                return null
            }
        }

        if (!addToCart) return null

        try {
            const itemsAdded = await addToCart(
                productData.map((product) => {
                    return {
                        product: product.masterProduct,
                        variant: product.variant,
                        quantity: 1,
                    }
                }),
            )

            // Open modal only when `addToCart` returns some data
            // It's possible that the item has been added to cart, but we don't want to open the modal.
            // See wishlist_primary_action for example.
            callback(itemsAdded)
            if (itemsAdded && showUniversalFeedback) {
                // If the products have been added successfully, show the confirmation modal
                onAddToCartModalOpen({
                    itemsAdded,
                    selectedQuantity: 1,
                    isBundle: !!bundleID,
                })
            }
            if (itemsAdded) {
                if (isFromHelpMeChoose) {
                    await addLayer(
                        basic_gtm_event.bind(this, {
                            event: 'help_me_choose',
                            eventCategory: 'engagement',
                            eventAction: 'help_me_choose-product_addToCart',
                            eventLabel:
                                productData[0]?.masterProduct?.c_variants?.[0]?.productId ||
                                productData[0]?.variant?.productId,
                        }),
                    )
                }
                await addLayer(polish_ecommerce_gtm_event.bind(this))
                await addLayer(
                    item_gtm_event.bind(this, {
                        event: 'add_to_cart',
                        currency: currency,
                        bundle: 'false',
                        items: itemsAdded.map((item) => ({
                            item_name:
                                item?.product?.c_defaultName || item?.masterProduct?.c_defaultName,
                            item_id: getItemId(item),
                            item_category:
                                item?.product?.c_defaultCategoryName ||
                                item?.masterProduct?.c_defaultCategoryName,
                            item_variant:
                                item?.product?.c_subModelIdentifier || item?.variant?.productId,
                            price: parseFloat(
                                (
                                    item?.variant?.price /
                                    (1 +
                                        (parseFloat(
                                            item?.product?.c_taxRate ??
                                                item?.masterProduct?.c_taxRate,
                                        ) || 0) /
                                            100)
                                ).toFixed(2),
                            ),
                            fullprice: getPriceData(item?.product)?.isOnSale
                                ? 'reduced'
                                : 'fullprice',
                            rating_points: '',
                            erp_category:
                                item?.product?.c_materialGroup ||
                                item?.masterProduct?.representedProduct?.c_materialGroup,
                            prdha_category:
                                item?.product?.c_prdha_category ||
                                item?.masterProduct?.c_prdha_category,
                            quantity: 1,
                        })),
                    }),
                )
            }
            if (itemsAdded && isSubscription && universalFeedback) {
                onAddToCartModalClose()
            }
        } catch (e) {
            showError() // Show error if there is a problem
        }
        setIsLoadingAddToCart(false) // Remove loading after request
    }

    const {isProductDetail} = useUtils().currentPage(location.pathname)

    return (
        <Button
            className="js-cart-add"
            data-testid="add-to-cart-button"
            data-product-code={
                product?.representedProduct?.id ||
                productsArray.map((product) => product.productId).join(',')
            }
            w="full"
            {...(universalFeedback ? {...styles.recommendedAdd} : {})}
            variant={universalFeedback ? 'secondary' : compactView ? 'primary-small' : 'primary'}
            onClick={(e) => {
                if (
                    !isProductDetail &&
                    typeof cartTriggerMCP !== 'undefined' &&
                    typeof cartTriggerMCP === 'function'
                ) {
                    window.cartTriggerMCP(e.target)
                }
                e.stopPropagation() // To prevent select_item event of the tile component
                handleCartItem(productData)
            }}
            isLoading={isLoadingAddToCart}
            sx={
                universalFeedback && {
                    ...styles.recommendedAdd,
                    maxH: isPurchaseBuy
                        ? {base: remCalc(40), lg: remCalc(40)}
                        : {base: remCalc(33), lg: remCalc(40)},
                    height: isPurchaseBuy
                        ? {base: remCalc(40), lg: remCalc(40)}
                        : {base: remCalc(33), lg: remCalc(40)},
                }
            }
        >
            {isFromHelpMeChoose ? (
                <FormattedMessage id="global.shop_now" defaultMessage="Shop Now" />
            ) : isPreorder ? (
                <FormattedMessage id="global.preorder_now" defaultMessage="Preorder now" />
            ) : (
                <FormattedMessage id="global.add-to-cart" defaultMessage="Add to cart" />
            )}
        </Button>
    )
}

AddToCartButton.propTypes = {
    product: PropTypes.oneOfType([
        PropTypes.shape({
            type: PropTypes.object,
            productType: PropTypes.object,
            representedProduct: PropTypes.object,
            masterProduct: PropTypes.object,
            variationAttributes: PropTypes.array,
            c_productSetProducts: PropTypes.array,
        }),
        PropTypes.arrayOf(
            PropTypes.shape({
                type: PropTypes.object,
                productType: PropTypes.object,
                representedProduct: PropTypes.object,
                masterProduct: PropTypes.object,
                variationAttributes: PropTypes.array,
                c_productSetProducts: PropTypes.array,
            }),
        ),
    ]).isRequired,
    productIndex: PropTypes.number,
    compactView: PropTypes.bool,
    setShowErrorMessage: PropTypes.func,
    errorContainerRef: PropTypes.object,
    showUniversalFeedback: PropTypes.bool,
    variationAttributes: PropTypes.array,
    bundleID: PropTypes.string,
    callback: PropTypes.func,
    universalFeedback: PropTypes.bool,
    isSubscription: PropTypes.bool,
    isFromHelpMeChoose: PropTypes.bool,
    isPurchaseBuy: PropTypes.bool,
    isPreorder: PropTypes.bool,
}

AddToCartButton.displayName = 'AddToCartButton'

export default AddToCartButton
